import { getElementPropertyResolvedCountByNameOrId } from '../../../helpers/element-property.helpers';
import { getBuildingLifetime } from '../../../helpers/project.helpers';
import {
  ElementCategoryID,
  IElementCategory,
} from '../../../models/element-categories.model';
import {
  ElementPropertyType,
  IFactoryProperty,
} from '../../../models/element-property.model';
import {
  getConversionFactorValue,
  relocateConservativeA1A3Delta,
  removeConversionFactorsByKey,
} from '../../../helpers/conversion-factors.helpers';
import { CONSERVATIVE_B2_UNIT } from '../../../models/unit.model';
import { EXPRESSION_VARIABLE_NAME } from '@shared/constants/expression-variables-names.constant';

export enum MaintenancePropertyName {
  Reoccurance = 'reoccurance',
}

/**
 * No sbef or lifetime property on maintenance
 */
const properties: IFactoryProperty[] = [
  {
    name: MaintenancePropertyName.Reoccurance,
    type: ElementPropertyType.Expression,
    min: 0,
    fallbackCount: `${EXPRESSION_VARIABLE_NAME.BUILDING_LIFETIME} / 2`,
    unit: 'year',
  },
];

/**
 * Legacy B2 mapper for elements wrapped in the Maintenance category
 * ({@link ElementCategoryID.Maintenance}).
 *
 * Scales the entire A-stage (co2e_A / sek_A) by buildingLifetime/reoccurance cycles.
 * Wrapped descendants use this legacy schablon; IVL B2/B4 from product characteristics
 * ({@link mapResultsByMaintenance}, {@link mapResultsByLifetime}) are skipped when an
 * ancestor is a Maintenance category element.
 *
 * Skips when co2e_B2 or sek_B2 is already truthy on the incoming results — note that
 * an explicit `co2e_B2: 0` is falsy and does not block this mapper.
 */
const mapResults: IElementCategory['mapResults'] = (results, context) => {
  const { project, categoryElement } = context;

  // Already applied B2, make sure we don't apply it again
  if (results.co2e_B2 || results.sek_B2) {
    return results;
  }

  const reoccurance = getElementPropertyResolvedCountByNameOrId(
    categoryElement,
    MaintenancePropertyName.Reoccurance,
    0,
  );

  const lifetime = getBuildingLifetime(project);

  // How many times the maintenance is needed before the building is demolished (use decimal method to spread the cost more evenly)
  const cycles = Math.max(lifetime / reoccurance, 0);

  return relocateConservativeA1A3Delta(
    removeConversionFactorsByKey(
      {
        ...results,
        co2e_B2: getConversionFactorValue(results, 'co2e_A') * cycles,
        sek_B2: getConversionFactorValue(results, 'sek_A') * cycles,
      },
      'co2e_A',
      'sek_A',
      'co2e_B4', // no lifetime co2e in maintenance
      'sek_B4', // no lifetime cost in maintenance
    ),
    CONSERVATIVE_B2_UNIT,
    cycles,
  );
};

export const maintenance: IElementCategory = {
  id: ElementCategoryID.Maintenance,
  name: 'Maintenance',
  cursiveName: true,
  properties,
  mapResults,
  getElementPropertySelectionByProductId: () => undefined,
};
